/**
 * 카드 이용대금 명세서(이메일 명세서 .htm) 파서. 2026-10-02.
 *
 * 지금은 신한카드 형식만 읽는다. 명세서의 '카드이용내역' 표(table.cardUseList)에서
 * 이용 1건 = tbody 1개(2줄 행)이고, 카드별 소계(tr.middleSum)·총합계(tfoot)는 건너뛴다.
 * 명세서 파일은 EUC-KR 이라 UTF-8 로 안 읽히면 EUC-KR 로 다시 읽는다.
 */

export interface CardUseRow {
  /** 이용일자 yyyymmdd */
  dt_use: string;
  /** 가맹점명 */
  nm_store: string;
  /** 소재지 */
  dc_addr: string;
  /** 업종 */
  nm_biztype: string;
  /** 이용금액 */
  am_use: number;
  /** 청구금액 */
  am_bill: number;
}

/** 파일 고르기 창을 띄워 고른 파일을 돌려준다. 취소하면 null */
export const pickFile = (accept: string): Promise<File | null> =>
  new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.addEventListener("cancel", () => resolve(null));
    input.click();
  });

const readHtmlText = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer();
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder("euc-kr").decode(buffer);
  }
};

/** "20,000 " -> 20000 */
const toAmount = (text: string | null | undefined): number => {
  const n = Number(String(text ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

/** "26.08.03" -> "20260803" */
const toDate = (text: string | null | undefined): string => {
  const m = String(text ?? "").match(/(\d{2,4})\.(\d{1,2})\.(\d{1,2})/);
  if (!m) return "";
  const yyyy = m[1].length === 2 ? `20${m[1]}` : m[1];
  return `${yyyy}${m[2].padStart(2, "0")}${m[3].padStart(2, "0")}`;
};

/** "가맹점명<br>(소재지/업종)" 셀을 나눈다 */
const splitStore = (td: Element) => {
  const cell = td.cloneNode(true) as Element;
  cell.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  const [name = "", detail = ""] = (cell.textContent ?? "")
    .split("\n")
    .map((s) => s.replace(/ /g, " ").trim());
  const inner = detail.replace(/^\(/, "").replace(/\)$/, "");
  const slash = inner.lastIndexOf("/");
  return {
    nm_store: name,
    dc_addr: (slash >= 0 ? inner.slice(0, slash) : inner).trim(),
    nm_biztype: slash >= 0 ? inner.slice(slash + 1).trim() : "",
  };
};

/** 신한카드 명세서 html 에서 카드이용내역을 읽는다. 표가 없으면 에러 */
export const parseShinhanCardStatement = async (
  file: File
): Promise<CardUseRow[]> => {
  const html = await readHtmlText(file);
  const doc = new DOMParser().parseFromString(html, "text/html");
  const table = doc.querySelector("table.cardUseList");
  if (!table) {
    throw new Error("신한카드 명세서의 카드이용내역을 찾을 수 없습니다.");
  }

  const rows: CardUseRow[] = [];
  table.querySelectorAll(":scope > tbody").forEach((tbody) => {
    const trs = Array.from(tbody.querySelectorAll(":scope > tr")).filter(
      (tr) => !tr.classList.contains("middleSum")
    );
    // 1줄: 이용일자·가맹점·주유량·할부기간·유가보조금·청구금액·할부잔액
    // 2줄: 이용금액·할부회차·주유할인
    const first = trs[0]?.querySelectorAll(":scope > td");
    const second = trs[1]?.querySelectorAll(":scope > td");
    if (!first || first.length < 6) return;

    const dt_use = toDate(first[0].textContent);
    if (!dt_use) return;

    rows.push({
      dt_use,
      ...splitStore(first[1]),
      am_use: toAmount(second?.[0]?.textContent),
      am_bill: toAmount(first[5].textContent),
    });
  });
  return rows;
};
